import { AlertTriangle, ListChecks } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { DatePicker, Select } from "antd";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

import { ContainerCard, EmployeeDetails, ModuleTitleCard } from "../../components/ui/cards";
import { Reactable } from "../../components/ui/reactable";
import { Button } from "../../components/ui/button";
import { DialogModal } from "../../components/ui/dialogModal";
import { modalFieldLabelClass } from "../../components/ui/fieldStyles";
import { Input, Textarea } from "../../components/ui/input";
import { truncateText } from "../../components/common/CommonFunctions";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { ABSCONDING_URL, EMPLOYEE_PERSONAL_INFO } from "../../constants/api";
import { useOrgAdminBranchColumn } from "../../hooks/useOrgAdminBranchColumn";

const StatusBadge = ({ status }) => {
    const config = {
        pending: "bg-yellow-100 text-yellow-700 border-yellow-200",
        approved: "bg-green-100 text-green-700 border-green-200",
        rejected: "bg-red-100 text-red-700 border-red-200",
    };
    const cls = config[status] || "bg-blue-100 text-blue-700 border-blue-200";
    const text = status ? status.charAt(0).toUpperCase() + status.slice(1) : "Pending";
    return (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${cls}`}>
            {text}
        </span>
    );
};

const formatDate = (value) => {
    if (!value) return "-";
    const d = dayjs(value);
    return d.isValid() ? d.format("DD/MM/YYYY") : "-";
};

const AbscondingList = () => {
    const { fetch, create } = useApiMethods();
    const navigate = useNavigate();
    const { showBranchColumn, insertBranchColumn, TempTransferBadge } = useOrgAdminBranchColumn("employee");

    const [tableData, setTableData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [serverTrigger, setServerTrigger] = useState(false);
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [employeeOptions, setEmployeeOptions] = useState([]);
    const [formState, setFormState] = useState({
        employee_id: undefined,
        last_present_date: dayjs(),
        consecutive_absents: "",
        remarks: "",
    });

    const fetchRef = useRef(fetch);
    fetchRef.current = fetch;

    const resetForm = () => {
        setFormState({
            employee_id: undefined,
            last_present_date: dayjs(),
            consecutive_absents: "",
            remarks: "",
        });
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const apiData = await fetchRef.current(EMPLOYEE_PERSONAL_INFO, { drop_down: "True", is_active: "True" });
                if (cancelled) return;
                if (apiData?.status === "success") {
                    const rows = Array.isArray(apiData.data) ? apiData.data : apiData.data?.result || [];
                    setEmployeeOptions(rows.filter((e) => e.is_active !== false));
                }
            } catch (error) {
                if (!cancelled) ToastMsg("error", error.message);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const handleRaise = async () => {
        if (!formState.employee_id) {
            ToastMsg("error", "Employee is required");
            return;
        }
        if (!formState.last_present_date) {
            ToastMsg("error", "Last present date is required");
            return;
        }
        if (!formState.remarks?.trim()) {
            ToastMsg("error", "Remarks are required");
            return;
        }
        setLoading(true);
        try {
            const payload = {
                employee_id: formState.employee_id,
                last_present_date: dayjs(formState.last_present_date).format("YYYY-MM-DD"),
                remarks: formState.remarks.trim(),
            };
            if (formState.consecutive_absents !== "") {
                payload.consecutive_absents = parseInt(formState.consecutive_absents, 10);
            }
            const response = await create(ABSCONDING_URL, payload);
            if (response?.status === "success") {
                ToastMsg("success", response.message || "Abscond case raised");
                setOpenDialog(false);
                resetForm();
                const caseId = response.data?.id;
                if (caseId) {
                    navigate("/employee/resignationrequest/process", {
                        state: { resignationId: caseId, stepKey: 1 },
                    });
                    return;
                }
                setServerTrigger((v) => !v);
            } else {
                ToastMsg("error", response?.message || "Failed to raise abscond");
            }
        } catch (error) {
            ToastMsg("error", error.message || "Failed to raise abscond");
        } finally {
            setLoading(false);
        }
    };

    const columns = useMemo(() => insertBranchColumn([
        {
            accessor: "employee",
            Header: "Employee",
            sortable: true,
            filterable: true,
            fixed: true,
            width: "250px",
            Cell: ({ value, row }) => {
                const record = value || {};
                const empDetails = {
                    ...record,
                    designation_id: record?.designation
                        ? { id: record.designation.id, designation_name: record.designation.name }
                        : { id: 0, designation_name: "" },
                    first_name: record?.employee_name || "-",
                    id: record?.employee_id || "-",
                };
                return (
                    <div className="flex items-center gap-2">
                        <EmployeeDetails details={empDetails} size="xs" />
                        {row?.on_temp_transfer && !showBranchColumn && <TempTransferBadge />}
                    </div>
                );
            },
        },
        {
            accessor: "last_present_date",
            Header: "Last present",
            Cell: ({ value }) => formatDate(value),
        },
        {
            accessor: "consecutive_absents",
            Header: "Absents",
            Cell: ({ value }) => (value === null || value === undefined || value === "" ? "-" : value),
        },
        {
            accessor: "is_payroll_hold",
            Header: "Payroll hold",
            Cell: ({ value }) => (value ? "On hold" : "Not on hold"),
        },
        {
            accessor: "stage",
            Header: "Stage",
        },
        {
            accessor: "approval_status",
            Header: "Status",
            Cell: ({ value }) => <StatusBadge status={value} />,
        },
        {
            accessor: "reason",
            Header: "Remarks",
            Cell: ({ value }) => (value ? truncateText(value, 20, "280px", "balance") : "-"),
        },
        {
            Header: "Action",
            fixed: true,
            align: "right",
            Cell: ({ row }) => (
                <Button
                    variant="ghost"
                    size="xs"
                    title="Run exit process"
                    onClick={() =>
                        navigate("/employee/resignationrequest/process", {
                            state: { resignationId: row.id, stepKey: 1 },
                        })
                    }
                    className="h-7 w-7 p-0 rounded-md bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100"
                >
                    <ListChecks className="h-3.5 w-3.5" />
                </Button>
            ),
        },
    ]), [insertBranchColumn, navigate, showBranchColumn, TempTransferBadge]);

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={AlertTriangle}
                title="Abscond"
                subtitle="Raise unnotified exits. Payroll hold and login disable happen immediately."
                totalRecords={totalRecords}
                buttonEnable
                setOpenDialog={(open) => {
                    if (open) resetForm();
                    setOpenDialog(open);
                }}
                buttonName="Raise Abscond"
            />

            <Reactable
                columns={columns}
                data={tableData}
                setData={setTableData}
                setTotalCount={setTotalRecords}
                serverSide
                url={ABSCONDING_URL}
                serverTrigger={serverTrigger}
                rowSize="md"
            />

            <DialogModal
                open={openDialog}
                onClose={() => {
                    setOpenDialog(false);
                    resetForm();
                }}
                onSave={handleRaise}
                title="Raise abscond"
                saveLabel="Raise"
                width="560px"
                loading={loading}
            >
                <div className="space-y-4 p-1">
                    <div className="space-y-2">
                        <label className={modalFieldLabelClass}>
                            Employee <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={formState.employee_id}
                            onChange={(value) => setFormState((prev) => ({ ...prev, employee_id: value }))}
                            placeholder="Select employee"
                            className="w-full"
                            size="large"
                            showSearch
                            optionFilterProp="children"
                        >
                            {employeeOptions.map((opt) => (
                                <Select.Option key={opt.id} value={opt.id}>
                                    {`${opt.first_name || ""} ${opt.last_name || ""} ${opt.employee_id ? "- " + opt.employee_id : ""}`}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className={modalFieldLabelClass}>
                                Last present date <span className="text-red-500">*</span>
                            </label>
                            <DatePicker
                                value={formState.last_present_date}
                                onChange={(date) => setFormState((prev) => ({ ...prev, last_present_date: date }))}
                                format="DD-MM-YYYY"
                                className="w-full"
                                size="large"
                                disabledDate={(d) => d && d > dayjs().endOf("day")}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className={modalFieldLabelClass}>
                                Consecutive absents
                            </label>
                            <Input
                                value={formState.consecutive_absents}
                                onChange={(e) =>
                                    setFormState((prev) => ({
                                        ...prev,
                                        consecutive_absents: e.target.value.replace(/[^0-9]/g, ""),
                                    }))
                                }
                                placeholder="Optional"
                            />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <label className={modalFieldLabelClass}>
                            Remarks <span className="text-red-500">*</span>
                        </label>
                        <Textarea
                            value={formState.remarks}
                            onChange={(e) => setFormState((prev) => ({ ...prev, remarks: e.target.value }))}
                            rows={4}
                            maxLength={250}
                            showCharCount
                            placeholder="Last known contact, shift, or how the absence was confirmed"
                        />
                    </div>
                </div>
            </DialogModal>
        </ContainerCard>
    );
};

export default AbscondingList;
